import { useEffect, useState } from 'react';
import { api } from './api';

/**
 * ═══ WHICH WAYS THIS SCHOOL MAY TAKE A REGISTER ═══
 *
 * The menu has to know before it draws itself, and every screen that mentions the photo roll or the
 * scanner has to agree with it. So: one request per session, shared.
 *
 * ⚠ A MODULE-LEVEL CACHE AND NOT A CONTEXT, on purpose. A provider would have to be mounted inside
 * AuthProvider and outside every consumer, and the only thing it would add is another wrapper in
 * App.tsx to forget. This is fetched once, shared by everyone who asks, and — the part that matters
 * — invalidated by the settings screen, so switching the scanner on makes it appear in the menu
 * without a reload.
 *
 * ⚠ NAMES, NOT BITS. The server sends "CardScan", not 4. The day a fifth mode is added, a browser
 * running a cached bundle and doing bit arithmetic is quietly wrong; one comparing strings is not.
 */

export type CaptureMode = 'NameList' | 'PhotoRoll' | 'CardScan' | 'Face';

export interface InstitutionCaptureModes {
  institutionId: string;
  name: string;
  modes: CaptureMode[];
  faceRequiresParentConsent: boolean;
}
export interface CaptureModesPayload {
  /** The union across the tenant's active institutions — what the menu should carry. */
  modes: CaptureMode[];
  /** Every mode this SERVER build knows. */
  available: CaptureMode[];
  institutions: InstitutionCaptureModes[];
}

/**
 * ⚠ THE FALLBACK IS THE SERVER'S OWN DEFAULT, not "everything" and not "nothing".
 *
 * If the request fails we are not entitled to guess that a school switched the scanner on, and we
 * are certainly not entitled to take the photo roll away because one request 500'd. The default is
 * what a school that has never opened settings has, so it is the honest answer to "we could not
 * ask".
 */
const FALLBACK: CaptureMode[] = ['NameList', 'PhotoRoll'];

let cache: CaptureModesPayload | null = null;
let inflight: Promise<void> | null = null;
const subscribers = new Set<() => void>();

function publish() { for (const fn of subscribers) fn(); }

function load(): Promise<void> {
  if (inflight) return inflight;
  inflight = api.get<CaptureModesPayload>('/api/attendance/policy/capture-modes')
    .then((p) => {
      // An older API build has no such route; `modes` absent means unknown, not empty.
      cache = p?.modes?.length
        ? p
        : { modes: FALLBACK, available: p?.available ?? FALLBACK, institutions: p?.institutions ?? [] };
    })
    .catch(() => { cache = { modes: FALLBACK, available: FALLBACK, institutions: [] }; })
    .finally(() => { inflight = null; publish(); });
  return inflight;
}

/** Throw the cached answer away and ask again — call after saving the settings. */
export function refreshCaptureModes(): void {
  cache = null;
  void load();
}

export interface CaptureModesState {
  modes: CaptureMode[];
  /** False until the server has answered — see the note on `allows`. */
  resolved: boolean;
  institutions: InstitutionCaptureModes[];
  available: CaptureMode[];
  /**
   * ⚠ FALSE WHILE UNRESOLVED. A menu entry that appears a beat late is invisible; one that vanishes
   * as somebody reaches for it is a bug report. The name list is never gated on this, so there is
   * always a way to take a register in that first beat.
   */
  allows: (mode: CaptureMode) => boolean;
}

/**
 * @param enabled Pass false for users who are not staff — the endpoint is staff-only, and a parent
 * firing a request that 403s on every page load is noise in the log and a wasted round trip.
 */
export function useCaptureModes(enabled = true): CaptureModesState {
  const [, bump] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const fn = () => bump((n) => n + 1);
    subscribers.add(fn);
    if (!cache) void load();
    return () => { subscribers.delete(fn); };
  }, [enabled]);

  const resolved = !enabled || cache !== null;
  const modes = cache?.modes ?? [];
  return {
    modes,
    resolved,
    institutions: cache?.institutions ?? [],
    available: cache?.available ?? FALLBACK,
    allows: (mode) => modes.includes(mode),
  };
}
